"use client";

import { useEffect } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { useUser } from "@/features/auth/hooks/useUser";
import { ApiAccessPitch } from "./ApiAccessPitch";
import { BTN_SECONDARY, CARD } from "./agenticStyle";
import { ConnectPanel } from "./ConnectPanel";
import { KeysPanel } from "./KeysPanel";
import { UsagePanel } from "./UsagePanel";
import { useAgenticOverview } from "./useAgenticOverview";
import { useClock } from "./useClock";

function Skeleton() {
  return (
    <div className="space-y-6" aria-busy="true" aria-label="Loading">
      {[180, 320].map((h) => (
        <div key={h} className={`${CARD} animate-pulse`} style={{ height: h }} />
      ))}
    </div>
  );
}

function Notice({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className={CARD}>
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#f5c518]" aria-hidden />
        <div>
          <h2 className="font-semibold text-white">{title}</h2>
          <div className="mt-1 text-sm text-[#c7d5e0]">{children}</div>
        </div>
      </div>
    </section>
  );
}

/**
 * The API dashboard (/agentic): keys, usage and agent setup for Studio (and
 * admins), what Studio's API access gets you for everyone else.
 * Client-rendered from /api/agentic/overview; the page around it is a static
 * shell.
 */
export function AgenticDashboard() {
  const { user, plan, loading: userLoading } = useUser();
  const { overview, error, loading, load, createKey, renameKey, revokeKey } = useAgenticOverview();
  const nowMs = useClock();
  const userId = user?.id ?? null;

  // Reload when the account or its plan changes (an upgrade from this page).
  useEffect(() => {
    if (userId) void load();
  }, [userId, plan, load]);

  if (userLoading || (userId && !overview && !error) || nowMs === null) return <Skeleton />;

  if (!userId) {
    return (
      <div className="space-y-6">
        <ApiAccessPitch signedIn={false} />
        <ConnectPanel />
      </div>
    );
  }

  if (!overview) {
    return (
      <Notice title="Couldn't load your API dashboard">
        <p>{error}</p>
        <button type="button" onClick={() => void load()} className={`${BTN_SECONDARY} mt-3`} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} aria-hidden />
          Try again
        </button>
      </Notice>
    );
  }

  if (!overview.entitled) {
    // Keys outlive the plan: a lapsed or cancelled owner still sees theirs
    // (paused) and can revoke them; they work again if Studio comes back.
    const hasKeys = overview.keys.some((k) => !k.revokedAt);
    return (
      <div className="space-y-6">
        {hasKeys && <KeysPanel overview={overview} nowMs={nowMs} actions={{ createKey, renameKey, revokeKey }} paused />}
        <ApiAccessPitch signedIn />
        <ConnectPanel />
      </div>
    );
  }

  if (!overview.ready) {
    return (
      <Notice title="API keys are almost here">
        <p>The API is being switched on. Check back shortly; nothing on your account needs doing.</p>
      </Notice>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <p className="rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-sm text-red-200" role="alert">
          {error}
        </p>
      )}
      {overview.isAdmin && overview.plan !== "studio" && (
        <p className="rounded-lg border border-[#2a475e] bg-[#16202d] px-3 py-2 text-xs text-[#8f98a0]">
          You see this as an admin. Admin keys get Studio&apos;s API limits and are metered like any other.
        </p>
      )}
      <KeysPanel overview={overview} nowMs={nowMs} actions={{ createKey, renameKey, revokeKey }} />
      <UsagePanel overview={overview} nowMs={nowMs} />
      <ConnectPanel />
    </div>
  );
}
